
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const ProteccionesAcabados = () => {
  const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
  const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
  const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

  const [images, setImages] = useState<string[]>([]);

  useEffect(() => {
    const loadImages = async () => {
      try {
        const { data, error } = await supabase.storage
          .from('HormiPremium')
          .list('Proteccion', {
            limit: 6,
            sortBy: { column: 'name', order: 'asc' },
          });

        if (error) throw error;

        if (data) {
          const imageFiles = data
            .filter((file) => {
              const ext = file.name.toLowerCase();
              return ext.endsWith('.jpg') || ext.endsWith('.jpeg') || ext.endsWith('.png');
            })
            .slice(0, 6)
            .map((file) => {
              const fileName = encodeURIComponent(file.name);
              return `${SUPABASE_URL}/storage/v1/object/public/HormiPremium/Proteccion/${fileName}`;
            });

          setImages(imageFiles);
        }
      } catch (error) {
        console.error('Error loading images:', error);
      }
    };

    loadImages();
  }, []);

  const features = [
    {
      icon: 'ri-shield-star-line',
      title: 'Protección Duradera',
      description: 'Selladores de alta calidad que protegen contra el desgaste',
    },
    {
      icon: 'ri-drop-line',
      title: 'Impermeabilización',
      description: 'Evita la penetración de agua y humedad en el pavimento',
    },
    {
      icon: 'ri-sun-foggy-line',
      title: 'Resistencia UV',
      description: 'Protege contra la decoloración causada por el sol',
    },
    {
      icon: 'ri-paint-brush-line',
      title: 'Realza el Color',
      description: 'Intensifica y mantiene los colores originales del hormigón',
    },
  ];

  const applications = [
    'Hormigón impreso',
    'Hormigón desactivado',
    'Hormigón pulido',
    'Hormigón rallado',
    'Piscinas Arelite',
    'Pavimentos exteriores',
  ];

  return (
    <section id="protecciones-acabados" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-shield-star-line"></i>
              <span>Protección Profesional</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-dark mb-6 leading-tight">
              Protecciones y Acabados
            </h2>
            <p className="text-secondary text-lg leading-relaxed mb-8">
              Las <strong>protecciones y acabados profesionales</strong> son esenciales para garantizar la <strong>durabilidad y belleza</strong> de tu pavimento de hormigón. Nuestros selladores de alta calidad protegen contra el desgaste, la humedad y los rayos UV.
            </p>
            <p className="text-secondary text-lg leading-relaxed mb-8">
              Aplicamos productos especializados que no solo protegen, sino que también <strong>realzan el color</strong> y el acabado original, manteniendo tu inversión en perfectas condiciones durante años.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${feature.icon} text-violet-600 text-xl`}></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-dark mb-1">{feature.title}</h4>
                    <p className="text-secondary text-sm">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <Link
              to="/servicios/protecciones-acabados"
              className="inline-flex items-center gap-2 px-8 py-4 bg-violet-600 text-white rounded-full font-semibold hover:bg-violet-700 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
            >
              Ver detalles completos
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </Link>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {images.length > 0 ? (
              images.slice(0, 4).map((image, index) => (
                <div
                  key={index}
                  className={`rounded-xl overflow-hidden shadow-lg ${
                    index === 0 ? 'col-span-2 h-80' : 'h-64'
                  }`}
                >
                  <img
                    src={image}
                    alt={`Protecciones y Acabados ${index + 1}`}
                    className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500"
                  />
                </div>
              ))
            ) : (
              <div className="col-span-2 h-80 bg-gray-200 rounded-xl flex items-center justify-center">
                <p className="text-gray-500">Cargando imágenes...</p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-gray-50 rounded-2xl p-8 md:p-12 shadow-lg">
          <h3 className="text-2xl font-bold text-dark mb-6">
            Aplicaciones de Protecciones y Acabados
          </h3>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {applications.map((app, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-4 bg-white rounded-lg"
              >
                <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-violet-600 font-bold"></i>
                </div>
                <span className="text-dark font-medium">{app}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProteccionesAcabados;
